import React from "react";

/**
 * Stockaily Brand System — v1.1
 * ────────────────────────────────────────────────────────────────────────
 * Renders the officially approved Stockaily brand marks.
 *
 * v1.1 (polish pass): The web derivatives are now real transparent PNGs
 * cropped tightly to the actual artwork, so they no longer show a black
 * rectangle on non-black surfaces:
 *   • navbar-wordmark-primary-transparent.png (640×130, ~60KB, aspect 4.93:1)
 *   • logo-symbol-transparent.png            (320×401, ~85KB, aspect 0.80:1)
 *
 * The `surface` prop is retained as a hint but is no longer strictly
 * required, since the transparent derivatives blend on any surface.
 *
 * Originals with baked-in backgrounds remain preserved verbatim at
 * /app/frontend/brand-originals/ (repo-only, NOT copied into the CRA
 * production build). They remain available for future light-document
 * surfaces if a true light variant of the wordmark ever ships.
 *
 * TODO(brand-v2): Replace PNG sources with a true SVG master when it
 * becomes available. Component API can stay identical.
 */

// Cache-busting suffix so a redeploy invalidates browser copies of the
// old assets. Bumped for v1.1 so the transparent derivatives are picked
// up even if a browser cached the old rectangular PNGs.
const V = "?v=brandv1_1";

const BRAND_ASSETS = Object.freeze({
  // Real transparent PNGs (tight crops of the approved artwork).
  wordmark: `/brand/navbar-wordmark-primary-transparent.png${V}`,
  symbol: `/brand/logo-symbol-transparent.png${V}`,
  // Legacy black-background variants are still shipped but not wired.
  // They remain available for surfaces we can’t guarantee are dark.
  wordmarkLegacyDark: `/brand/navbar-wordmark-primary.png${V}`,
  symbolLegacyDark: `/brand/logo-symbol-dark-bg.png${V}`,
  symbolLight: `/brand/logo-symbol-light-bg.png${V}`,
  marketingGlow: `/brand/marketing-glow-symbol.png${V}`,
});

// Intrinsic dimensions of the transparent derivatives — used to reserve
// the correct aspect ratio slot before the bytes arrive (no layout shift).
const WORDMARK_INTRINSIC = { w: 640, h: 130 };   // aspect 4.923
const SYMBOL_INTRINSIC = { w: 320, h: 401 };     // aspect 0.798

const WORDMARK_ASPECT = WORDMARK_INTRINSIC.w / WORDMARK_INTRINSIC.h;
const SYMBOL_ASPECT = SYMBOL_INTRINSIC.w / SYMBOL_INTRINSIC.h;

/**
 * StockailyWordmark — horizontal "symbol + STOCKAILY" lockup.
 *
 * @param {number}  height     Rendered pixel height (width auto).
 * @param {boolean} decorative When true the image is hidden from
 *                             assistive tech (alt="", aria-hidden).
 */
export function StockailyWordmark({
  height = 26,
  decorative = false,
  className = "",
  ...rest
}) {
  const width = Math.round(height * WORDMARK_ASPECT);
  return (
    <img
      src={BRAND_ASSETS.wordmark}
      width={width}
      height={height}
      alt={decorative ? "" : "Stockaily"}
      aria-hidden={decorative || undefined}
      draggable={false}
      decoding="async"
      loading="eager"
      style={{ width: `${width}px`, height: `${height}px`, display: "block" }}
      className={className}
      data-brand="wordmark"
      {...rest}
    />
  );
}

/**
 * StockailySymbol — standalone glyph. Real transparent PNG so it drops
 * cleanly onto any theme without a rectangular halo.
 *
 * @param {number}  height     Rendered pixel height (width auto, aspect 0.80:1).
 * @param {boolean} decorative See StockailyWordmark.
 *
 * Legacy `surface` prop retained for backwards compatibility but ignored
 * for the transparent asset. If callers explicitly pass
 * `surface="light-bg-only"`, the caller acknowledges they are on a light
 * surface and wants the black-on-white light variant instead.
 */
export function StockailySymbol({
  height = 48,
  size,             // legacy alias for height; keeps older callers working
  surface,          // legacy; ignored (transparent asset works on any surface)
  decorative = false,
  className = "",
  ...rest
}) {
  const h = size ?? height;
  const w = Math.round(h * SYMBOL_ASPECT);
  const src =
    surface === "light-bg-only" ? BRAND_ASSETS.symbolLight : BRAND_ASSETS.symbol;
  return (
    <img
      src={src}
      width={w}
      height={h}
      alt={decorative ? "" : "Stockaily"}
      aria-hidden={decorative || undefined}
      draggable={false}
      decoding="async"
      loading="eager"
      style={{ width: `${w}px`, height: `${h}px`, display: "block" }}
      className={className}
      data-brand="symbol"
      {...rest}
    />
  );
}

/**
 * StockailyBrand — thin convenience wrapper.
 */
export default function StockailyBrand({ variant = "wordmark", ...rest }) {
  if (variant === "symbol") return <StockailySymbol {...rest} />;
  return <StockailyWordmark {...rest} />;
}
